import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
  FormGroup,
} from "reactstrap";
import Cleave from "cleave.js/react";
import CommonCheckbox from "../../../../Common/CommonCheckbox";
import Dropzone from "react-dropzone";
import SvgIcon from "../../../../CommonComponents/SvgIcon/SvgIcon";
import { Breadcrumbs, P, H6 } from "../../../../../AbstractElements";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, Fragment, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import {
  industriesService,
  INDUSTRY_QUERY_KEY,
} from "../../../../../Services/industriesService";
import { Industries } from "../../../../../Types/MasterData/Industries";
import {
  countryService,
  COUNTRY_QUERY_KEY,
} from "../../../../../Services/countryService";
import { Country } from "../../../../../Types/MasterData/Country";
import {
  provinceService,
  PROVINCE_QUERY_KEY,
} from "../../../../../Services/provinceService";
import { Province } from "../../../../../Types/MasterData/Province";
import {
  cityService,
  CITY_QUERY_KEY,
} from "../../../../../Services/cityService";
import { City } from "../../../../../Types/MasterData/City";
import {
  barangayService,
  BARANGAY_QUERY_KEY,
} from "../../../../../Services/barangayService";
import { Barangay } from "../../../../../Types/MasterData/Barangay";
import {
  companyprofileService,
  VIEW_COMPANY_QUERY_KEY,
} from "../../../../../Services/companyProfileService";

const INITIAL_STATE = {
  company_code: "",
  company_name: "",
  owner_name: "",
  start_date: "",
  industry: "",
  address_line1: "",
  address_line2: "",
  logo_url: "",
  country: "",
  province: "",
  city: "",
  barangay: "",
  zip_code: "",
  status: -1,
  billing_address_line1: "",
  billing_address_line2: "",
  billing_country: "",
  billing_province: "",
  billing_city: "",
  billing_barangay: "",
  billing_zip_code: "",
  tin_id: "",
  created_by: undefined,
  updated_by: undefined,
};

const CreateClientProfileForm = ({ toggle }: { toggle?: () => void }) => {
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(INITIAL_STATE);

  //Logo Upload
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const onDrop = (acceptedFiles: File[]) =>
    setUploadedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  const removeFile = (indexToRemove: number) =>
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove),
    );

  // LOV Industries
  const { data: industryData, isLoading: isIndustryLoading } = useQuery<{
    data: Industries[];
  }>({
    queryKey: INDUSTRY_QUERY_KEY,
    queryFn: industriesService.getAllIndustries,
  });

  const industries: Industries[] =
    industryData?.data?.filter((item) => item.status === 1) ?? [];

  // LOV Country
  const { data: countryData, isLoading: isCountryLoading } = useQuery<{
    data: Country[];
  }>({
    queryKey: COUNTRY_QUERY_KEY,
    queryFn: countryService.getAllCountry,
  });

  const countries: Country[] =
    countryData?.data?.filter((item) => item.status === 1) ?? [];

  //Get Province By Country
  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [countryBillCode, setCountryBillCode] = useState<string | null>(null);

  const handleProvinceSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      province: "",
      city: "",
      barangay: "",
      zip_code: "",
    }));
    setCountryCode(e.target.value);
    setProvinceId("");
    setCityId("");
  };

  const handleBillProvinceSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      billing_province: "",
      billing_city: "",
      billing_barangay: "",
      billing_zip_code: "",
    }));
    setCountryBillCode(e.target.value);
    setProvinceBillId("");
    setCityBillId("");
  };

  const {
    data: provinceData,
    isLoading: isProvinceLoading,
    isError: isProvinceError,
  } = useQuery<{ data: Province[] }>({
    queryKey: [PROVINCE_QUERY_KEY, countryCode],
    queryFn: () => provinceService.getProvinceByCountry(countryCode as string),
    enabled: !!countryCode,
  });

  const {
    data: provinceBillData,
    isLoading: isProvinceBillLoading,
    isError: isProvinceBillError,
  } = useQuery<{ data: Province[] }>({
    queryKey: [PROVINCE_QUERY_KEY, countryBillCode],
    queryFn: () =>
      provinceService.getProvinceByCountry(countryBillCode as string),
    enabled: !!countryBillCode,
  });

  const provinces: Province[] =
    provinceData?.data?.filter((item) => item.status === 1) ?? [];

  const provinceOptions = !isProvinceError
    ? provinces.map((prov) => ({
        id: prov.id,
        label: prov.name,
      }))
    : [];

  const billprovinces: Province[] =
    provinceBillData?.data?.filter((item) => item.status === 1) ?? [];

  const billprovinceOptions = !isProvinceBillError
    ? billprovinces.map((prov) => ({
        id: prov.id,
        label: prov.name,
      }))
    : [];

  //Get City By Province
  const [provinceId, setProvinceId] = useState<string | null>(null);
  const [provinceBillId, setProvinceBillId] = useState<string | null>(null);

  const handleCitySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      city: "",
      barangay: "",
      zip_code: "",
    }));
    setProvinceId(e.target.value);
    setCityId("");
  };

  const handleBillCitySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      billing_city: "",
      billing_barangay: "",
      billing_zip_code: "",
    }));
    setProvinceBillId(e.target.value);
    setCityBillId("");
  };

  const {
    data: cityData,
    isLoading: isCityLoading,
    isError: isCityError,
  } = useQuery<{ data: City[] }>({
    queryKey: [CITY_QUERY_KEY, provinceId],
    queryFn: () => cityService.getCityByProvince(provinceId as string),
    enabled: !!provinceId,
  });

  const cities: City[] =
    cityData?.data?.filter((item) => item.status === 1) ?? [];

  const {
    data: cityBillData,
    isLoading: isCityBillLoading,
    isError: isCityBillError,
  } = useQuery<{ data: City[] }>({
    queryKey: [CITY_QUERY_KEY, provinceBillId],
    queryFn: () => cityService.getCityByProvince(provinceBillId as string),
    enabled: !!provinceBillId,
  });

  const Billcities: City[] =
    cityBillData?.data?.filter((item) => item.status === 1) ?? [];

  const cityOptions = !isCityError
    ? cities.map((city) => ({
        id: city.id,
        label: city.name,
      }))
    : [];

  const cityBillOptions = !isCityBillError
    ? Billcities.map((city) => ({
        id: city.id,
        label: city.name,
      }))
    : [];

  //Get Barangay By City
  const [cityId, setCityId] = useState<string | null>(null);
  const [cityBillId, setCityBillId] = useState<string | null>(null);

  const handleBarangaySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      barangay: "",
      zip_code: "",
    }));
    setCityId(e.target.value);
  };

  const {
    data: barangayData,
    isLoading: isBarangayLoading,
    isError: isBarangayError,
  } = useQuery<{ data: Barangay[] }>({
    queryKey: [BARANGAY_QUERY_KEY, cityId],
    queryFn: () => barangayService.getBarangayByCity(cityId as string),
    enabled: !!cityId,
  });

  const barangay: Barangay[] =
    barangayData?.data?.filter((item) => item.status === 1) ?? [];

  const barangayOptions = !isCityError
    ? barangay.map((barangay) => ({
        id: barangay.id,
        label: barangay.name,
      }))
    : [];

  const handleBarangayBillSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      billing_barangay: "",
      billing_zip_code: "",
    }));
    setCityBillId(e.target.value);
  };

  const {
    data: barangayBillData,
    isLoading: isBarangayBillLoading,
    isError: isBarangayBillError,
  } = useQuery<{ data: Barangay[] }>({
    queryKey: [BARANGAY_QUERY_KEY, cityBillId],
    queryFn: () => barangayService.getBarangayByCity(cityBillId as string),
    enabled: !!cityBillId,
  });

  const Billbarangay: Barangay[] =
    barangayBillData?.data?.filter((item) => item.status === 1) ?? [];

  const barangayBillOptions = !isBarangayBillError
    ? Billbarangay.map((barangay) => ({
        id: barangay.id,
        label: barangay.name,
      }))
    : [];

  const [useBusinessAddress, setUseBusinessAddress] = useState(false);

  const handleBillingAddress = () => {
    setUseBusinessAddress((prev) => !prev);
  };

  // Auto-fill billing fields when useBusinessAddress is true
  useEffect(() => {
    if (useBusinessAddress) {
      setFormData((prev) => ({
        ...prev,
        billing_address_line1: formData.address_line1,
        billing_address_line2: formData.address_line2,
        billing_country: formData.country,
        billing_province: formData.province,
        billing_city: formData.city,
        billing_barangay: formData.barangay,
        billing_zip_code: formData.zip_code,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        billing_address_line1: "",
        billing_address_line2: "",
        billing_country: "",
        billing_province: "",
        billing_city: "",
        billing_barangay: "",
        billing_zip_code: "",
      }));
    }
  }, [useBusinessAddress, setFormData]);

  const createCompanyProfileMutation = useMutation<
    any,
    any, // error type
    FormData // input type for .mutate()
  >({
    mutationFn: (formData: FormData) =>
      companyprofileService.createCompanyProfile(formData), // pass FormData
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: VIEW_COMPANY_QUERY_KEY,
      });
      toast.success(
        "The company billing address has been successfully updated.",
      );
      setFormData(INITIAL_STATE);
      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        const errorMessage = responseData.errors[firstErrorKey][0];
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error(
          "Failed to create company profile. Please check your inputs.",
        );
      }
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(useBusinessAddress
        ? {
            billing_address_line1:
              name === "address_line1" ? value : prev.billing_address_line1,
            billing_address_line2:
              name === "address_line2" ? value : prev.billing_address_line2,
            billing_country: name === "country" ? value : prev.billing_country,
            billing_province:
              name === "province" ? value : prev.billing_province,
            billing_city: name === "city" ? value : prev.billing_city,
            billing_barangay:
              name === "barangay" ? value : prev.billing_barangay,
            billing_zip_code:
              name === "zip_code" ? value : prev.billing_zip_code,
          }
        : {}),
    }));
  };

  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.status === -1) {
      toast.warn("Please select a Status");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, String(value));
      }
    });

    if (uploadedFiles.length > 0) {
      data.append("logo_url", uploadedFiles[0]);
    }

    data.append("created_by", String(user.id));
    data.append("updated_by", String(user.id));

    createCompanyProfileMutation.mutate(data);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <H6 className="fw-bold">Step 1: Company Information</H6>
          <Col md="2">
            <Label>
              Company Code<b className="text-danger">*</b>
            </Label>
            <Input
              id="company_code"
              name="company_code"
              type="text"
              placeholder="XXX"
              required
              maxLength={3}
              minLength={3}
              value={formData.company_code}
              onChange={handleInputChange}
              style={{ textTransform: "uppercase" }}
            />
          </Col>
          <Col md="10">
            <Label>
              Company Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="company_name"
              name="company_name"
              type="text"
              placeholder="Enter New Civil Status"
              maxLength={50}
              minLength={3}
              required
              value={formData.company_name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="2">
            <Label>
              Start Date<b className="text-danger">*</b>
            </Label>
            <Input
              id="start_date"
              name="start_date"
              type="date"
              required
              value={formData.start_date}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="4">
            <Label>Chief Executive Officer</Label>
            <Input
              id="owner_name"
              name="owner_name"
              type="text"
              placeholder="Enter Founder or Contact Person"
              maxLength={150}
              minLength={5}
              value={formData.owner_name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="3">
            <Label>
              Industry<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.industry}
              id="industry"
              name="industry"
              type="select"
              onChange={handleInputChange}
              required
            >
              <option value="" disabled>
                {isIndustryLoading ? "Loading..." : "Select Industry"}
              </option>
              {!isIndustryLoading &&
                industries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="3">
            <Label>TIN</Label>
            <Cleave
              className="form-control"
              options={{
                delimiter: "-",
                blocks: [3, 3, 3, 3],
                uppercase: true,
              }}
              id="tin_id"
              name="tin_id"
              type="text"
              placeholder="Enter Company TIN"
              maxLength={150}
              minLength={5}
              value={formData.tin_id}
              onChange={handleInputChange}
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col md="12"></Col>
          <H6 className="fw-bold">Step 2: Business Address</H6>

          <Col md="6">
            <Label>
              Address Line 1<b className="text-danger">*</b>
            </Label>
            <Input
              id="address_line1"
              name="address_line1"
              type="text"
              placeholder="Street / House / Building"
              maxLength={150}
              minLength={5}
              required
              value={formData.address_line1}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="3">
            <Label>Address Line 2</Label>
            <Input
              id="address_line2"
              name="address_line2"
              type="text"
              placeholder="Apartment / Unit / Subdivision"
              maxLength={150}
              minLength={5}
              value={formData.address_line2}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="3">
            <Label>
              Country<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2 select-scroll"
              value={formData.country}
              id="country"
              name="country"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleProvinceSelect(e);
              }}
              required
            >
              <option value="" disabled>
                {isCountryLoading ? "Loading..." : "Select Country"}
              </option>
              {!isCountryLoading &&
                countries.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="3">
            <Label>
              Province<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.province}
              id="province"
              name="province"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleCitySelect(e);
              }}
              required
              disabled={isProvinceLoading || !countryCode || isProvinceError}
            >
              <option value="" selected>
                {isProvinceLoading
                  ? "Loading..."
                  : isProvinceError
                    ? "No Province found"
                    : "Select Province"}
              </option>

              {!isProvinceLoading &&
                provinceOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="3">
            <Label>
              City/Municipality<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.city}
              id="city"
              name="city"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
                handleBarangaySelect(e);
              }}
              required
              disabled={isCityLoading || !provinceId || isCityError}
            >
              <option value="" disabled selected>
                {isCityLoading
                  ? "Loading..."
                  : isCityError
                    ? "No City Found"
                    : "Select City"}
              </option>

              {!isCityLoading &&
                cityOptions.map((opt) => (
                  <option key={opt.id || "empty"} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="3">
            <Label>
              Barangay<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.barangay}
              id="barangay"
              name="barangay"
              type="select"
              onChange={(e) => {
                handleInputChange(e);
              }}
              required
              disabled={isBarangayLoading || !cityId || isBarangayError}
            >
              <option value="" disabled selected>
                {isBarangayLoading
                  ? "Loading..."
                  : isCityError
                    ? "No Barangay Found"
                    : "Select Barangay"}
              </option>

              {!isBarangayLoading &&
                barangayOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
            </Input>
          </Col>

          <Col md="3">
            <Label>
              ZIP Code<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              id="zip_code"
              name="zip_code"
              type="text"
              placeholder="XXXX"
              maxLength={4}
              minLength={4}
              value={formData.zip_code}
              onChange={handleInputChange}
              required
            />
          </Col>
        </Row>
        <Row className="g-3">
          <Col md="12"></Col>
          <H6 className="fw-bold">Step 3: Billing Address</H6>
          <FormGroup check switch>
            <Input
              type="checkbox"
              id="flexSwitchCheckDefault"
              check={useBusinessAddress}
              onChange={handleBillingAddress}
            />
            <Label htmlFor="flexSwitchCheckDefault" check>
              Use Business Address for Billing?
            </Label>
          </FormGroup>
          {!useBusinessAddress && (
            <>
              <Col md="6">
                <Label>
                  Address Line 1<b className="text-danger">*</b>
                </Label>
                <Input
                  id="billing_address_line1"
                  name="billing_address_line1"
                  type="text"
                  placeholder="Street / House / Building"
                  maxLength={150}
                  minLength={5}
                  required
                  value={formData.billing_address_line1}
                  onChange={handleInputChange}
                />
              </Col>

              <Col md="3">
                <Label>Address Line 2</Label>
                <Input
                  id="billing_address_line2"
                  name="billing_address_line2"
                  type="text"
                  placeholder="Apartment / Unit / Subdivision"
                  maxLength={150}
                  minLength={5}
                  value={formData.billing_address_line2}
                  onChange={handleInputChange}
                />
              </Col>

              <Col md="3">
                <Label>
                  Country<b className="text-danger">*</b>
                </Label>
                <Input
                  className="form-control mb-2 select-scroll"
                  value={formData.billing_country}
                  id="billing_country"
                  name="billing_country"
                  type="select"
                  onChange={(e) => {
                    handleInputChange(e);
                    handleBillProvinceSelect(e);
                  }}
                  required
                >
                  <option value="" disabled>
                    {isCountryLoading ? "Loading..." : "Select Country"}
                  </option>
                  {!isCountryLoading &&
                    countries.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.name}
                      </option>
                    ))}
                </Input>
              </Col>

              <Col md="3">
                <Label>
                  Province<b className="text-danger">*</b>
                </Label>
                <Input
                  className="form-control mb-2"
                  value={formData.billing_province}
                  id="billing_province"
                  name="billing_province"
                  type="select"
                  onChange={(e) => {
                    handleInputChange(e);
                    handleBillCitySelect(e);
                  }}
                  required
                  disabled={
                    isProvinceBillLoading ||
                    !countryBillCode ||
                    isProvinceBillError
                  }
                >
                  <option value="" selected>
                    {isProvinceBillLoading
                      ? "Loading..."
                      : isProvinceBillError
                        ? "No Province found"
                        : "Select Province"}
                  </option>

                  {!isProvinceBillLoading &&
                    billprovinceOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                </Input>
              </Col>

              <Col md="3">
                <Label>
                  City/Municipality<b className="text-danger">*</b>
                </Label>
                <Input
                  className="form-control mb-2"
                  value={formData.billing_city}
                  id="billing_city"
                  name="billing_city"
                  type="select"
                  onChange={(e) => {
                    handleInputChange(e);
                    handleBarangayBillSelect(e);
                  }}
                  required
                  disabled={
                    isCityBillLoading || !provinceBillId || isCityBillError
                  }
                >
                  <option value="" disabled selected>
                    {isCityBillLoading
                      ? "Loading..."
                      : isCityBillError
                        ? "No City Found"
                        : "Select City"}
                  </option>

                  {!isCityBillLoading &&
                    cityBillOptions.map((opt) => (
                      <option key={opt.id || "empty"} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                </Input>
              </Col>

              <Col md="3">
                <Label>
                  Barangay<b className="text-danger">*</b>
                </Label>
                <Input
                  className="form-control mb-2"
                  value={formData.billing_barangay}
                  id="billing_barangay"
                  name="billing_barangay"
                  type="select"
                  onChange={(e) => {
                    handleInputChange(e);
                  }}
                  required
                  disabled={
                    isBarangayBillLoading || !cityBillId || isBarangayBillError
                  }
                >
                  <option value="" disabled selected>
                    {isBarangayBillLoading
                      ? "Loading..."
                      : isBarangayBillError
                        ? "No Barangay Found"
                        : "Select Barangay"}
                  </option>

                  {!isBarangayBillLoading &&
                    barangayBillOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                </Input>
              </Col>

              <Col md="3">
                <Label>
                  ZIP Code<b className="text-danger">*</b>
                </Label>
                <Input
                  className="form-control mb-2"
                  id="billing_zip_code"
                  name="billing_zip_code"
                  type="text"
                  placeholder="XXXX"
                  maxLength={4}
                  minLength={4}
                  value={formData.billing_zip_code}
                  onChange={handleInputChange}
                  required
                />
              </Col>
            </>
          )}
        </Row>
        <Row className="g-3">
          <Col md="12"></Col>
          <H6 className="fw-bold">Step 4: Other Information</H6>

          <Col md="3">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>

          <Col md="9">
            <Label>
              Company Logo<b className="text-danger">*</b>
            </Label>
            <Fragment>
              <Dropzone
                onDrop={onDrop}
                maxFiles={1} // allow only one file
                accept={{
                  "image/jpeg": [".jpeg", ".jpg"],
                  "image/png": [".png"],
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()} className="dropzone-container">
                    <input {...getInputProps()} />
                    {uploadedFiles.length === 0 ? (
                      <div className="dz-message needsclick">
                        <SvgIcon
                          iconId="file-upload1"
                          style={{ width: 24, height: 24 }}
                        />
                        <H6>
                          Drag File or Click to Browse (JPEG, JPG, PNG only)
                        </H6>
                      </div>
                    ) : (
                      <div className="uploaded-files">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="file-card">
                            {file.type.startsWith("image/") ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="file-thumbnail"
                              />
                            ) : (
                              <div className="file-placeholder">
                                {file.name.split(".").pop()?.toUpperCase()} File
                              </div>
                            )}
                            <p className="file-name">{file.name}</p>
                            <p className="file-size">
                              {(file.size / 1024).toFixed(2)} KB
                            </p>
                            <button
                              onClick={() => removeFile(index)}
                              className="remove-button"
                              title="Remove file"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Dropzone>
            </Fragment>
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={createCompanyProfileMutation.isPending}
            >
              {createCompanyProfileMutation.isPending
                ? "Creating Client Profile..."
                : "Create"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateClientProfileForm;
